// Pure helpers for the ChatWidget: the open/closed decision, its sessionStorage
// persistence, and the mapping from our WidgetMessage into assistant-ui's shape.
// Kept side-effect-light and dependency-free so they're unit-testable in isolation.
import type { ButtonAction, WidgetMessage } from "./types";
import type { ThreadMessageLike } from "@assistant-ui/react";

const OPEN_STATE_KEY = "si-chat-widget-open";

export type StoredOpenState = "open" | "closed" | null;

// Decide whether the widget starts open on mount. Default is closed; it opens
// only when there IS prior history AND the user last left it open. A blank
// stored value falls back to closed.
export function decideInitialOpen({ hasHistory, stored }: { hasHistory: boolean; stored: StoredOpenState }): boolean {
  return hasHistory && stored === "open";
}

export function readStoredOpenState(): StoredOpenState {
  const value = window.sessionStorage.getItem(OPEN_STATE_KEY);
  return value === "open" || value === "closed" ? value : null;
}

export function writeStoredOpenState(open: boolean): void {
  window.sessionStorage.setItem(OPEN_STATE_KEY, open ? "open" : "closed");
}

// Map a WidgetMessage to assistant-ui's message shape. Assistant replies carry
// their buttons on metadata.custom.buttons — the SAME shape the live adapter
// emits — so rehydrated and live affordances render identically.
export function toThreadMessage(message: WidgetMessage): ThreadMessageLike {
  const buttons: ButtonAction[] = message.buttons ?? [];
  return {
    id: message.id,
    role: message.role,
    content: [{ type: "text", text: message.text }],
    metadata: message.role === "assistant" ? { custom: { buttons, messageId: message.id } } : undefined,
  };
}
